import React, { useMemo } from 'react'
import { Trans } from 'react-i18next'
import { Alert, Spin, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck'
import { faArrowRotateRight } from '@fortawesome/pro-solid-svg-icons/faArrowRotateRight'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight'
import { faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { useTranslationRankmi } from 'helpers/translations.helper'
import {
  AiEvaluationPreviewMeta,
  AiEvaluationQuestion,
} from 'api/administration/lessons/aiEvaluation'
import { groupQuestionsByBlock } from './questionUtils'
import {
  BlockCard,
  BlockGroupsList,
  BlockTag,
  BlockTagSparkle,
  BlockTagText,
  CorrectAnswerIcon,
  DeleteQuestionButton,
  FooterActions,
  FooterCancelButton,
  FooterDisclaimer,
  FooterRegenerateButton,
  FooterRegenerateIcon,
  FooterRegenerateLabel,
  MetaCard,
  MetaField,
  MetaLabel,
  MetaValue,
  MetaValueRow,
  OptionLabel,
  OptionRow,
  OptionsList,
  PreviewAlertWrap,
  PreviewFooter,
  PreviewHeaderCopy,
  PreviewIntro,
  PreviewLayout,
  PreviewScrollArea,
  PreviewSummary,
  QuestionActions,
  QuestionItem,
  QuestionItemRegenerating,
  QuestionTitle,
  RegenerateQuestionButton,
  RegenerateQuestionIcon,
  RegenerateQuestionLabel,
  SaveAndConfigureButton,
  SaveButtonIcon,
} from './EvaluationPreview.styled'

interface Props {
  questions: AiEvaluationQuestion[]
  blockCount: number
  meta: AiEvaluationPreviewMeta | null
  name: string
  description: string
  validationError: boolean
  errorMessage: string | null
  onDismissError: () => void
  onDeleteQuestion: (clientId: string) => void
  onRegenerateQuestion: (clientId: string) => void
  onCancel: () => void
  onRegenerateAll: () => void
  onConfirm: () => void
}

const EvaluationPreviewContent: React.FC<Props> = ({
  questions,
  blockCount,
  meta,
  name,
  description,
  validationError,
  errorMessage,
  onDismissError,
  onDeleteQuestion,
  onRegenerateQuestion,
  onCancel,
  onRegenerateAll,
  onConfirm,
}) => {
  const t = useTranslationRankmi({
    prefix: 'administration.blocks.aiEvaluation.previewModal',
  })

  const blockGroups = useMemo(
    () => groupQuestionsByBlock(questions),
    [questions]
  )

  const questionNumberByClientId = useMemo(() => {
    const map = new Map<string, number>()
    questions.forEach((question, index) => {
      if (question.clientId) {
        map.set(question.clientId, index + 1)
      }
    })
    return map
  }, [questions])

  const questionCount = questions.length

  const summaryQuestionsLabel = t(
    questionCount === 1 ? 'summaryQuestions_one' : 'summaryQuestions_other',
    { count: questionCount }
  )
  const summaryGeneratedLabel = t(
    questionCount === 1 ? 'summaryGenerated_one' : 'summaryGenerated_other'
  )
  const summaryBlocksLabel = t(
    blockCount === 1 ? 'summaryBlocks_one' : 'summaryBlocks_other',
    { count: blockCount }
  )

  return (
    <PreviewLayout>
      <PreviewScrollArea>
        <PreviewHeaderCopy>
          <PreviewIntro>
            <Trans
              i18nKey="administration.blocks.aiEvaluation.previewModal.intro"
              components={{ strong: <strong /> }}
            />
          </PreviewIntro>

          <PreviewSummary>
            <strong>{summaryQuestionsLabel}</strong>
            {` ${summaryGeneratedLabel} `}
            <strong>{summaryBlocksLabel}</strong>
          </PreviewSummary>
        </PreviewHeaderCopy>

        {errorMessage && (
          <PreviewAlertWrap role="alert">
            <Alert
              type="error"
              message={errorMessage}
              closable
              onClose={onDismissError}
            />
          </PreviewAlertWrap>
        )}

        <MetaCard>
          <MetaField>
            <MetaLabel>{t('evaluationNameLabel')}</MetaLabel>
            <MetaValueRow>
              <MetaValue $muted={!name.trim()}>
                {name.trim() || t('namePlaceholder')}
              </MetaValue>
            </MetaValueRow>
          </MetaField>
          <MetaField>
            <MetaLabel>{t('evaluationDescriptionLabel')}</MetaLabel>
            <MetaValueRow>
              <MetaValue $muted={!description.trim()}>
                {description.trim() || t('descriptionPlaceholder')}
              </MetaValue>
            </MetaValueRow>
          </MetaField>
        </MetaCard>

        {meta?.contentWarning && (
          <PreviewAlertWrap>
            <Alert type="info" message={meta.contentWarning} />
          </PreviewAlertWrap>
        )}

        <BlockGroupsList>
          {blockGroups.map((group) => (
            <BlockCard key={`block-${group.blockIndex}`}>
              <BlockTag>
                <BlockTagSparkle icon={faSparkles} aria-hidden />
                <BlockTagText>
                  {t('blockTagPrefix')}
                  <strong>
                    {' '}
                    {t('blockTagLabel', { index: group.blockIndex })}
                  </strong>
                  {group.blockTitle ? (
                    <>
                      {t('blockTagTitleSeparator')}
                      <strong>{group.blockTitle}</strong>
                    </>
                  ) : null}
                </BlockTagText>
              </BlockTag>

              {group.questions.map((question, questionIdx) => {
                const clientId = question.clientId!
                const displayNumber =
                  questionNumberByClientId.get(clientId) ?? questionIdx + 1

                if (question.regenerating) {
                  return (
                    <QuestionItem key={clientId} $withDivider={questionIdx > 0}>
                      <QuestionItemRegenerating>
                        <Spin tip={t('regeneratingQuestion')} />
                      </QuestionItemRegenerating>
                    </QuestionItem>
                  )
                }

                return (
                  <QuestionItem key={clientId} $withDivider={questionIdx > 0}>
                    <QuestionTitle>
                      {displayNumber}. {question.name}
                    </QuestionTitle>

                    <OptionsList>
                      {question.options.map((option, optionIdx) => (
                        <OptionRow key={`${clientId}-opt-${optionIdx}`}>
                          <OptionLabel>{option.label}</OptionLabel>
                          {option.correct && (
                            <Tooltip title={t('correctAnswerTooltip')}>
                              <CorrectAnswerIcon
                                icon={faCircleCheck}
                                aria-label={t('correctAnswerTooltip')}
                              />
                            </Tooltip>
                          )}
                        </OptionRow>
                      ))}
                    </OptionsList>

                    <QuestionActions>
                      <DeleteQuestionButton
                        type="button"
                        data-test-id={`button-ai-evaluation-delete-question-${
                          displayNumber - 1
                        }`}
                        onClick={() => onDeleteQuestion(clientId)}
                      >
                        {t('deleteQuestion')}
                      </DeleteQuestionButton>
                      <RegenerateQuestionButton
                        type="button"
                        data-test-id={`button-ai-evaluation-regenerate-question-${
                          displayNumber - 1
                        }`}
                        onClick={() => onRegenerateQuestion(clientId)}
                      >
                        <RegenerateQuestionIcon
                          icon={faArrowRotateRight}
                          aria-hidden
                        />
                        <RegenerateQuestionLabel>
                          {t('regenerateQuestion')}
                        </RegenerateQuestionLabel>
                      </RegenerateQuestionButton>
                    </QuestionActions>
                  </QuestionItem>
                )
              })}
            </BlockCard>
          ))}
        </BlockGroupsList>
      </PreviewScrollArea>

      <PreviewFooter>
        <FooterActions>
          <FooterCancelButton
            type="button"
            data-test-id="button-ai-evaluation-cancel"
            onClick={onCancel}
          >
            {t('cancel')}
          </FooterCancelButton>
          <FooterRegenerateButton
            type="button"
            data-test-id="button-ai-evaluation-regenerate-all"
            onClick={onRegenerateAll}
          >
            <FooterRegenerateIcon icon={faArrowRotateRight} aria-hidden />
            <FooterRegenerateLabel>{t('regenerateAll')}</FooterRegenerateLabel>
          </FooterRegenerateButton>
          <SaveAndConfigureButton
            type="button"
            data-test-id="button-ai-evaluation-commit"
            $disabled={!!validationError}
            onClick={onConfirm}
          >
            {t('saveAndConfigure')}
            <SaveButtonIcon icon={faArrowRight} aria-hidden />
          </SaveAndConfigureButton>
        </FooterActions>
        <FooterDisclaimer>{t('footerDisclaimer')}</FooterDisclaimer>
      </PreviewFooter>
    </PreviewLayout>
  )
}

export default EvaluationPreviewContent
